import { BD_FOOTER_IMAGE } from '../branding/footer-image';

/**
 * Configuration for footer dimensions
 */
export interface FooterConfig {
  /** Desired logo height in pixels */
  desiredLogoHeight: number;
  /** Padding around the logo in pixels */
  padding: number;
  /** Logo aspect ratio (width/height) - BinDoc logo: 2046/286 ≈ 7.15 */
  logoAspectRatio: number;
}

/**
 * Calculated footer dimensions
 */
export interface FooterDimensions {
  /** Width of the logo in pixels */
  logoWidth: number;
  /** Height of the logo in pixels */
  logoHeight: number;
  /** Total height of the footer (logo + padding) */
  footerHeight: number;
  /** Y position for the logo (vertically centered) */
  logoY: number;
  /** X position for the logo (left padding) */
  logoX: number;
}

/**
 * Default footer configuration for BinDoc exports
 */
export const BD_DEFAULT_FOOTER_CONFIG: FooterConfig = {
  desiredLogoHeight: 30,
  padding: 10,
  logoAspectRatio: 2046 / 286, // BinDoc logo dimensions
};

/**
 * Pure function to calculate footer dimensions based on canvas width and config
 * @param canvasWidth - Width of the source canvas
 * @param config - Footer configuration (defaults to BinDoc defaults)
 * @returns Calculated footer dimensions
 */
export function calculateFooterDimensions(
  canvasWidth: number,
  config: FooterConfig = BD_DEFAULT_FOOTER_CONFIG,
): FooterDimensions {
  const { desiredLogoHeight, padding, logoAspectRatio } = config;

  // Calculate max logo width that fits within canvas with padding
  const maxLogoWidth = canvasWidth - padding * 2;

  // Calculate logo dimensions maintaining aspect ratio
  let logoHeight = desiredLogoHeight;
  let logoWidth = Math.floor(logoHeight * logoAspectRatio);

  // If logo width exceeds canvas width, scale down based on width constraint
  if (logoWidth > maxLogoWidth) {
    logoWidth = maxLogoWidth;
    logoHeight = Math.floor(logoWidth / logoAspectRatio);
  }

  // Calculate footer height based on logo height plus padding
  const footerHeight = logoHeight + padding * 2;

  // Logo position: left padding, vertically centered
  const logoX = padding;
  const logoY = (footerHeight - logoHeight) / 2;

  return {
    logoWidth,
    logoHeight,
    footerHeight,
    logoY,
    logoX,
  };
}

/**
 * Options for creating a footer canvas
 */
export interface CreateFooterCanvasOptions {
  /** Width of the footer canvas */
  canvasWidth: number;
  /** Footer dimensions (from calculateFooterDimensions) */
  dimensions: FooterDimensions;
  /** Background color (default: white) */
  backgroundColor?: string;
  /** Footer image source (default: BD_FOOTER_IMAGE) */
  footerImageSrc?: string;
}

/**
 * Creates a footer canvas with the BinDoc logo
 * This is a pure function that returns a Promise resolving to a data URL
 *
 * @param document - Document object for creating canvas (allows dependency injection for testing)
 * @param options - Options for creating the footer
 * @returns Promise resolving to a data URL of the footer image
 */
export function createFooterCanvas(
  document: Document,
  options: CreateFooterCanvasOptions,
): Promise<string> {
  const {
    canvasWidth,
    dimensions,
    backgroundColor = '#ffffff',
    footerImageSrc = BD_FOOTER_IMAGE,
  } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvasWidth;
      tempCanvas.height = dimensions.footerHeight;

      const ctx = tempCanvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas 2d context'));
        return;
      }

      // Fill background
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, canvasWidth, dimensions.footerHeight);

      // Draw logo using the 9-argument form to explicitly specify source and destination
      // This ensures the entire source image is scaled correctly to the destination dimensions
      ctx.drawImage(
        img,
        0, // source X
        0, // source Y
        img.naturalWidth || img.width, // source width (use naturalWidth for reliability)
        img.naturalHeight || img.height, // source height
        dimensions.logoX, // destination X
        dimensions.logoY, // destination Y
        dimensions.logoWidth, // destination width
        dimensions.logoHeight, // destination height
      );

      resolve(tempCanvas.toDataURL('image/png'));
    };

    img.onerror = () => {
      reject(new Error('Failed to load footer image'));
    };

    img.src = footerImageSrc;
  });
}

/**
 * Options for merging a canvas with a footer
 */
export interface MergeWithFooterOptions {
  /** The source canvas to merge */
  sourceCanvas: HTMLCanvasElement;
  /** Footer configuration (optional, uses defaults) */
  footerConfig?: FooterConfig;
  /** Background color for the footer (default: white) */
  footerBackgroundColor?: string;
  /** Background color for the entire image (default: white). Set to 'transparent' to preserve transparency. */
  backgroundColor?: string;
}

/**
 * Result of a merge operation
 */
export interface MergeResult {
  /** The merged image as a data URL */
  dataUrl: string;
  /** Total width of the merged image */
  width: number;
  /** Total height of the merged image */
  height: number;
}

/**
 * Type for the merge-images library function
 */
export type MergeImagesFunction = (
  images: Array<{ src: string; x?: number; y?: number; opacity?: number }>,
  options: { width: number; height: number },
) => Promise<string>;

/**
 * Creates a solid color background canvas as a data URL
 *
 * @param document - Document object for creating canvas
 * @param width - Width of the background
 * @param height - Height of the background
 * @param color - Background color (CSS color string)
 * @returns Data URL of the solid color image
 */
export function createSolidColorBackground(
  document: Document,
  width: number,
  height: number,
  color: string,
): string {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, width, height);
  }

  return canvas.toDataURL('image/png');
}

/**
 * Merges a canvas with the BinDoc footer
 *
 * @param document - Document object for creating canvas
 * @param mergeImages - The merge-images library function (allows dependency injection for testing)
 * @param options - Merge options
 * @returns Promise resolving to the merge result
 */
export async function mergeCanvasWithFooter(
  document: Document,
  mergeImages: MergeImagesFunction,
  options: MergeWithFooterOptions,
): Promise<MergeResult> {
  const {
    sourceCanvas,
    footerConfig = BD_DEFAULT_FOOTER_CONFIG,
    footerBackgroundColor = '#ffffff',
    backgroundColor = '#ffffff',
  } = options;

  const canvasWidth = sourceCanvas.width;
  const canvasHeight = sourceCanvas.height;

  // Calculate footer dimensions
  const dimensions = calculateFooterDimensions(canvasWidth, footerConfig);

  // Create footer canvas
  const footerDataUrl = await createFooterCanvas(document, {
    canvasWidth,
    dimensions,
    backgroundColor: footerBackgroundColor,
  });

  // Merge images
  const totalHeight = canvasHeight + dimensions.footerHeight;

  // Build the images array - add background first if not transparent
  const imagesToMerge: Array<{
    src: string;
    x?: number;
    y?: number;
    opacity?: number;
  }> = [];

  // Add solid background color layer first (unless transparent)
  if (backgroundColor !== 'transparent') {
    imagesToMerge.push({
      src: createSolidColorBackground(
        document,
        canvasWidth,
        totalHeight,
        backgroundColor,
      ),
    });
  }

  // Add the source canvas
  imagesToMerge.push({ src: sourceCanvas.toDataURL('image/png') });

  // Add the footer
  imagesToMerge.push({
    src: footerDataUrl,
    x: 0,
    y: canvasHeight,
    opacity: 1,
  });

  const dataUrl = await mergeImages(imagesToMerge, {
    width: canvasWidth,
    height: totalHeight,
  });

  return {
    dataUrl,
    width: canvasWidth,
    height: totalHeight,
  };
}
